<template>
  <div class="bg-white p-0 md:p-8 space-y-8">
    <div class="flex items-start">
      <div class="flex items-center gap-4 mr-2.5">
        <div class="w-11 h-11 rounded-lg border border-zinc-200 flex items-center justify-center">
          <span class="text-lg font-semibold text-gray-700">Q{{ questionNumber }}</span>
        </div>
      </div>
      <span
          class="items-center text-base md:text-lg text-zinc-800 font-medium px-3 py-2 rounded-lg"
          :class="(isChoiceQuestion || isReviewed) ? 'bg-success/10' : 'bg-error/10'"
      >
        {{ (isChoiceQuestion || isReviewed) ? __('Gained:') : __('Maximum Gain:') }}
        {{ displayScore }}
        {{ __('points') }}
      </span>
    </div>

    <div class="text-zinc-800 text-base font-medium">{{ questionData.question.title }}</div>
    <div class="text-zinc-800 text-2xl font-medium">{{ __('Answer:') }}</div>

    <div class="space-y-6">
      <div v-if="isChoiceQuestion" class="space-y-6">
        <div
            v-for="answer in questionData.challenge_answers"
            :key="answer.id"
            class="relative p-4 md:p-6 rounded-lg border-2 transition-all flex items-center gap-3"
            :class="getAnswerClass(answer)"
        >
          <div class="flex-shrink-0">
            <div
                v-if="isUserSelected(answer.id)"
                class="w-5 h-5 rounded border-2 border-zinc-600 bg-white flex items-center justify-center"
            >
              <Icon name="check" class="w-4 h-4 !text-base items-center justify-center text-zinc-600" />
            </div>
            <div v-else class="w-5 h-5 rounded border-2 border-zinc-600"></div>
          </div>

          <span class="text-gray-700 text-base font-medium flex-1">{{ answer.text }}</span>

          <div v-if="isUserSelected(answer.id) && answer.is_correct"
               class="absolute top-0 -right-0.5 w-6 h-6 bg-success flex items-center justify-center rounded-tr-lg rounded-bl-lg">
            <Icon name="check" class="w-4 h-4 text-white !text-lg" />
          </div>
          <div v-else-if="isUserSelected(answer.id) && !answer.is_correct"
               class="absolute top-0 -right-0.5 w-6 h-6 bg-error flex items-center justify-center rounded-tr-lg rounded-bl-lg">
            <Icon name="close" class="w-4 h-4 text-white !text-lg" />
          </div>
        </div>
      </div>

      <div v-else class="space-y-6">
        <div class="space-y-4">
          <span class="text-base text-gray-600">{{ questionData.user_answer?.text_answer || __('No answer provided') }}</span>
        </div>

        <div class="space-y-4 md:pt-6">
          <div v-if="hasExistingFeedback && !isEditMode" class="relative text-sm text-gray-600 rounded-xl border-zinc-400 border p-4">
            <button
                @click="enableEditMode"
                class="absolute top-4 right-6 p-1 rounded transition"
                type="button"
            >
              <Icon name="edit" class="w-3 h-3 text-primary" is-filled/>
            </button>
            <div class="flex items-center gap-4">
              <img
                  class="h-11 w-11 rounded-full ring-2 ring-gray-200 self-start"
                  :src="recruiter.profile_photo"
                  :alt="recruiter.full_name"
              />
              <div class="flex flex-col items-start gap-1">
          <span class="text-xs text-zinc-800 font-medium">
            {{ recruiter.full_name }}
          </span>
                <span class="text-base text-zinc-600 pr-4">
            {{ savedFeedback }}
          </span>
              </div>
            </div>
          </div>

          <ReviewTextarea
              v-if="!hasExistingFeedback || isEditMode"
              id="review-feedback"
              :value="localFeedback"
              :placeholder="__('Add your review feedback...')"
              @update:value="val => localFeedback = val"
              @save="saveFeedback"
          />
        </div>

        <div class="space-y-4 border-t pt-6">
          <ScoreSlider
              v-model="localPoints"
              :max="questionData.question.points_per_question || questionData.max_points"
              @update:modelValue="handlePointsChange"
              @mark-perfect="onMarkPerfect"
          />
        </div>
      </div>
    </div>

    <div class="flex flex-col md:flex-row md:justify-between !mt-14 gap-3">
      <div class="order-1 md:order-2 md:ml-auto flex gap-3">
        <button
            v-if="!isLastQuestion"
            @click="shouldShowSaveButton ? handleSave() : handleNext()"
            type="button"
            class="w-full md:w-auto px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
            :disabled="false"
        >
          <span>{{ isSavingPoints ? __('Saving...') : (shouldShowSaveButton ? __('Save and Continue') : __('Next')) }}</span>
        </button>
        <button
            v-else
            @click="handleFinalSave"
            type="button"
            class="w-full md:w-auto px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          <span>{{ isSavingFinal ? __('Submitting...') : (allReviewed && !hasModifiedPoints ? __('Back to Challenge') : __('Submit')) }}</span>
        </button>
      </div>

      <button
          v-if="!isFirstQuestion"
          type="button"
          @click="handlePrevious"
          class="order-2 md:order-1 w-full md:w-auto inline-flex items-center justify-center md:justify-start px-6 py-2 hover-lift-no-shadow text-gray-700 hover:bg-gray-50 hover:rounded-md transition-colors"
      >
        <Icon name="arrow_back_ios" class="w-5 h-5 text-xl text-zinc-800" />
        {{ __('Back to previous question') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { Icon } from '@/components/block/icon';
import { __ } from '@/helpers';
import ReviewTextarea from "@/pages/company/challenges/components/ReviewTextarea.vue";
import ScoreSlider from "@/pages/company/challenges/components/ScoreSlider.vue";

const props = defineProps({
  questionData: {
    type: Object,
    required: true
  },
  questionNumber: {
    type: Number,
    required: true
  },
  challengeId: {
    type: Number,
    required: true
  },
  submissionId: {
    type: Number,
    required: true
  },
  isFirstQuestion: {
    type: Boolean,
    default: false
  },
  isLastQuestion: {
    type: Boolean,
    default: false
  },
  recruiter: {
    type: Object,
    required: true
  },
  currentScore: {
    type: Number,
    default: null
  },
  allReviewed : {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits(['next', 'previous', 'complete', 'score-saved']);
const toast = useToast();

const localPoints = ref(0);
const localFeedback = ref('');
const savedFeedback = ref('');
const isEditMode = ref(false);
const isSavingPoints = ref(false);
const pointsSaved = ref(false);
const originalScore = ref(null);
const hasModifiedPoints = ref(false);
const isSavingFinal = ref(false);

const isChoiceQuestion = computed(() => {
  const answerType = props.questionData.question.answer_type;
  return answerType === 1 || answerType === 2;
});

const hasExistingFeedback = computed(() => {
  return savedFeedback.value && savedFeedback.value.trim() !== '';
});

const currentScore = computed(() => {
  if (isChoiceQuestion.value) {
    return Number(props.questionData.user_answer?.score ?? 0);
  }

  if (props.currentScore !== null && props.currentScore !== undefined) {
    return Number(props.currentScore);
  }

  return Number(localPoints.value ?? 0);
});

const displayScore = computed(() => {
  if (isChoiceQuestion.value || isReviewed.value) {
    return currentScore.value;
  }
  return props.questionData.question.points_per_question || 0;
});

const isReviewed = computed(() => {
  const score = props.questionData.user_answer?.score;
  return score !== null && score !== undefined;
});

const shouldShowSaveButton = computed(() => {
  if (isChoiceQuestion.value) return false;
  return !isReviewed.value || hasModifiedPoints.value;
})

const isUserSelected = (answerId) => {
  if (!props.questionData.user_answer) return false;
  const answerIds = props.questionData.user_answer.answer_ids;
  if (!answerIds) return false;
  const idsArray = Array.isArray(answerIds) ? answerIds : [];
  return idsArray.includes(answerId);
};

const getAnswerClass = (answer) => {
  const selected = isUserSelected(answer.id);

  if (selected && answer.is_correct) {
    return 'border-success bg-success/10';
  } else if (selected && !answer.is_correct) {
    return 'border-error bg-white';
  } else if (!selected && answer.is_correct) {
    return 'border-success bg-white';
  }

  return 'border-gray-200 bg-white';
};

const enableEditMode = () => {
  isEditMode.value = true;
  localFeedback.value = savedFeedback.value;
};

const saveFeedback = async (feedback) => {
  try {
    const response = await axios.post(
        route('redesign.company.challenges.update-feedback', {
          challenge: props.challengeId,
          submission: props.submissionId,
          question: props.questionData.question.id
        }),
        { feedback: feedback }
    );

    if (response.data.success) {
      savedFeedback.value = feedback;
      localFeedback.value = feedback;
      isEditMode.value = false;
      toast.success(__('Feedback saved successfully'));
    }
  } catch (error) {
    toast.error(__('Failed to save feedback'));
  }
};

const handlePointsChange = (points) => {
  localPoints.value = points;
  if(!isChoiceQuestion.value && isReviewed.value) {
    hasModifiedPoints.value = points !== originalScore.value;
  }
};

const handleSave = async () => {
  isSavingPoints.value = true;
  try {
    const response = await axios.post(
        route('redesign.company.challenges.update-points', {
          challenge: props.challengeId,
          submission: props.submissionId,
          question: props.questionData.question.id
        }),
        { points: localPoints.value }
    );

    if (response.data.success) {
      pointsSaved.value = true;
      hasModifiedPoints.value = false;
      originalScore.value = localPoints.value;

      emit('score-saved', props.questionData.question.id, localPoints.value);

      toast.success(response.data.message);
      emit('next');
    }
  } catch (error) {
    toast.error(__('Failed to save points'));
  } finally {
    isSavingPoints.value = false;
  }
};

const onMarkPerfect = (perfectScore) => {
  localPoints.value = perfectScore;
};

const handleNext = () => {
  emit('next');
};

const handlePrevious = () => {
  emit('previous');
};

const handleFinalSave = async () => {
  isSavingFinal.value = true;
  if (!isChoiceQuestion.value && (!pointsSaved.value || hasModifiedPoints.value)) {
    isSavingPoints.value = true;

    try {
      const response = await axios.post(route('redesign.company.challenges.update-points', {
        challenge: props.challengeId,
        submission: props.submissionId,
        question: props.questionData.question.id
      }),
          { points: localPoints.value }
      );
      if (response.data.success) {
        pointsSaved.value = true;
        hasModifiedPoints.value = false;
        originalScore.value = localPoints.value;
        emit('score-saved', props.questionData.question.id, localPoints.value);
        toast.success(response.data.message);

        if (props.allReviewed) {
          window.location.href = route('redesign.company.challenges.show', {
            challenge: props.challengeId
          });
        } else {
          emit('complete');
        }
      }
    } catch (error) {
      toast.error(__('Failed to save points'));
      isSavingFinal.value = false;
    } finally {
      isSavingPoints.value = false;
    }
  } else {
    if (props.allReviewed) {
      window.location.href = route('redesign.company.challenges.show', {
        challenge: props.challengeId
      });
    } else {
      emit('complete');
    }

    isSavingFinal.value = false;
  }
};

const initializeReviewData = () => {
  hasModifiedPoints.value = false;

  if (props.currentScore !== null && props.currentScore !== undefined) {
    localPoints.value = props.currentScore;
    originalScore.value = props.currentScore;
    pointsSaved.value = true;
  } else if (props.questionData.user_answer) {
    if (!isChoiceQuestion.value) {
      const score = props.questionData.user_answer.score;
      localPoints.value = score !== null && score !== undefined ? score : 0;
      originalScore.value = score !== null && score !== undefined ? score : 0;
      savedFeedback.value = props.questionData.user_answer.feedback || '';
      localFeedback.value = '';

      if (props.questionData.user_answer.reviewed_at !== null) {
        pointsSaved.value = true;
      }
    }
  } else {
    localPoints.value = 0;
    originalScore.value = 0;
  }
};

onMounted(() => {
  initializeReviewData();
});

watch(() => props.questionData, () => {
  initializeReviewData();
  isEditMode.value = false;
}, { deep: true });

watch(localPoints, (newValue) => {
  if(!isChoiceQuestion && isReviewed.value) {
    hasModifiedPoints.value = newValue !== originalScore.value;
  }
});
</script>